// Pinia 인스턴스와 앱 공통 상태(useAppStore)를 정의. accessToken은 로그인으로 발급받은 JWT 값
import { createPinia, defineStore } from 'pinia'
import { createPersistedState } from 'pinia-plugin-persistedstate'
import { resolveSite } from '@/app/site'

interface IAppState {
  accessToken: string
  refreshToken: string
  userName: string
  userDesc: string
  // 로그인 사용자 식별자(로그인 응답 data.user.idx). 참여자는 본인 participant.idx와 같다. 0=미설정
  userIdx: number
  // 로그인 사용자 사진 URL(참여자만 사용, 아바타 표시용). 빈 문자열이면 이니셜로 폴백
  photoUrl: string
  // 과제 미열람(코멘트·신규제출) 존재 여부. 사이드바 '과제' 메뉴 New 태그용(영속화 제외, 런타임 갱신)
  assignmentUnread: boolean
  // 주말계획서 미열람 존재 여부. 사이드바 '주말계획서' 메뉴 New 태그용(영속화 제외, 런타임 갱신)
  weekendPlanUnread: boolean
  // 매니저/참여자 소속 학교명·타임존(로그인 응답에서 받아 저장). 상단바·사이드바 표기용.
  schoolName: string
  schoolTimeZone: string
}

export const useAppStore = defineStore('gfgf-app-store', {
  state: (): IAppState => ({
    accessToken: '',
    refreshToken: '',
    userName: '',
    userDesc: '',
    userIdx: 0,
    photoUrl: '',
    assignmentUnread: false,
    weekendPlanUnread: false,
    schoolName: '',
    schoolTimeZone: '',
  }),

  getters: {
    hasAuth(): boolean {
      return Boolean(this.accessToken)
    },
  },

  actions: {
    setTokens(accessToken: string, refreshToken: string): void {
      this.accessToken = accessToken
      this.refreshToken = refreshToken
    },

    // userIdx는 로그인 응답에서 받은 값을 저장(미지정 역할은 0). 본인 행 하이라이트 등에 사용
    // photoUrl은 참여자 아바타용(미지정 역할은 빈 문자열)
    setUser(userName: string, userDesc: string, userIdx = 0, photoUrl = ''): void {
      this.userName = userName
      this.userDesc = userDesc
      this.userIdx = userIdx
      this.photoUrl = photoUrl
    },

    // 과제 미열람 여부 갱신 — 상세에서 읽음처리할 때마다 호출해 사이드바 '과제' New 태그를 재평가한다.
    setAssignmentUnread(v: boolean): void {
      this.assignmentUnread = v
    },

    // 주말계획서 미열람 여부 갱신 — 상세 읽음처리 시 호출해 사이드바 '주말계획서' New 태그를 재평가한다.
    setWeekendPlanUnread(v: boolean): void {
      this.weekendPlanUnread = v
    },

    // 로그인 시 소속 학교명·타임존 저장(매니저/참여자). 상단바·사이드바 표기용.
    setSchool(schoolName: string, timeZone: string): void {
      this.schoolName = schoolName
      this.schoolTimeZone = timeZone
    },

    clearAuthData(): void {
      this.accessToken = ''
      this.refreshToken = ''
      this.userName = ''
      this.userDesc = ''
      this.userIdx = 0
      this.photoUrl = ''
      this.schoolName = ''
      this.schoolTimeZone = ''
    },
  },

  persist: {
    storage: localStorage,
    pick: ['accessToken', 'refreshToken', 'userName', 'userDesc', 'userIdx', 'photoUrl', 'schoolName', 'schoolTimeZone'],
  },
})

const pinia = createPinia()
// 영속화 키를 ${site}:${id}로 격리한다. site는 역할(도메인)간, id는 store($id)간 충돌을 막는다.
pinia.use(createPersistedState({ key: id => `${resolveSite()}:${id}` }))

export default pinia
